import { useEffect, useMemo, useState } from 'react';
import { bulkImportApi } from '../../../services/adminApi';
import { useBulkJobPolling } from '../../../hooks/useBulkJobPolling';
import ConfirmStep from '../bulk-import/ConfirmStep';
import DryRunStep from '../bulk-import/DryRunStep';
import BulkImportHelp from '../bulk-import/BulkImportHelp';
import ProgressStep from '../bulk-import/ProgressStep';
import ResultsStep from '../bulk-import/ResultsStep';
import UploadStep from '../bulk-import/UploadStep';

const CANONICAL_IMPORT_OPTIONS = {
  generateTags: true,
  forceUpdate: true,
  forceRetag: false,
};

const STEP_ITEMS = [
  { key: 'upload', label: '1. Upload' },
  { key: 'dryRun', label: '2. Dry-run' },
  { key: 'confirm', label: '3. Confirm' },
  { key: 'running', label: '4. Progress' },
  { key: 'results', label: '5. Results' },
];

const BulkImport = () => {
  const [step, setStep] = useState('upload');
  const [selectedFile, setSelectedFile] = useState(null);
  const [dryRunData, setDryRunData] = useState(null);
  const [jobId, setJobId] = useState(null);
  const [activeJobStatus, setActiveJobStatus] = useState(null);
  const [resultsData, setResultsData] = useState(null);
  const [resultFilter, setResultFilter] = useState('all');
  const [loading, setLoading] = useState(false);
  const [resultsLoading, setResultsLoading] = useState(false);
  const [retryLoading, setRetryLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchResults = async (targetJobId, filter = 'all') => {
    setResultsLoading(true);
    try {
      const result = await bulkImportApi.getJobResults(targetJobId, filter);
      setResultsData(result);
      setError(null);
    } catch (nextError) {
      setError(nextError.message || 'Failed to load import results');
    } finally {
      setResultsLoading(false);
    }
  };

  const { jobStatus, pollingError } = useBulkJobPolling({
    jobId,
    enabled: step === 'running' && Boolean(jobId),
    intervalMs: 2000,
    onTerminalStatus: async (terminalStatus) => {
      setActiveJobStatus(terminalStatus);
      await fetchResults(terminalStatus.id, resultFilter);
      setStep('results');
    },
  });

  useEffect(() => {
    if (jobStatus) {
      setActiveJobStatus(jobStatus);
    }
  }, [jobStatus]);

  useEffect(() => {
    if (step === 'results' && jobId) {
      fetchResults(jobId, resultFilter);
    }
  }, [resultFilter]);

  const failedCount = activeJobStatus?.counts?.failed || 0;
  const canRetry = step === 'results' && failedCount > 0 && activeJobStatus?.status !== 'running';

  const summaryCards = useMemo(() => {
    if (!dryRunData?.summary) return [];

    return [
      { label: 'Total Rows', value: dryRunData.summary.totalRaw },
      { label: 'Valid Rows', value: dryRunData.summary.validCount },
      { label: 'Invalid Rows', value: dryRunData.summary.invalidCount },
      { label: 'Insert Candidates', value: dryRunData.summary.insertCount },
      { label: 'Duplicates', value: dryRunData.summary.duplicateCount },
    ];
  }, [dryRunData]);

  const resetFlow = () => {
    setStep('upload');
    setSelectedFile(null);
    setDryRunData(null);
    setJobId(null);
    setActiveJobStatus(null);
    setResultsData(null);
    setResultFilter('all');
    setError(null);
  };

  const handleRunDryRun = async () => {
    if (!selectedFile) return;

    setLoading(true);
    setError(null);
    try {
      const response = await bulkImportApi.runDryRun(selectedFile);
      setDryRunData(response);
      setStep('dryRun');
    } catch (nextError) {
      setError(nextError.message || 'Dry-run failed');
    } finally {
      setLoading(false);
    }
  };

  const handleStartImport = async () => {
    if (!selectedFile) return;

    setLoading(true);
    setError(null);
    try {
      const response = await bulkImportApi.startImport(selectedFile, CANONICAL_IMPORT_OPTIONS);
      setJobId(response.jobId);
      setActiveJobStatus(null);
      setResultsData(null);
      setStep('running');
    } catch (nextError) {
      setError(nextError.message || 'Failed to start import');
    } finally {
      setLoading(false);
    }
  };

  const handleRetryFailed = async () => {
    if (!jobId) return;

    setRetryLoading(true);
    setError(null);
    try {
      await bulkImportApi.retryFailed(jobId, CANONICAL_IMPORT_OPTIONS.forceRetag);
      setResultsData(null);
      setStep('running');
    } catch (nextError) {
      setError(nextError.message || 'Failed to retry failed rows');
    } finally {
      setRetryLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <div className="flex flex-wrap items-center gap-3 mb-2">
          <h1 className="text-2xl font-bold text-text-primary">Bulk Import</h1>
          <span className="rounded-full bg-primary-50 px-3 py-1 text-xs font-medium text-primary-700">
            Excel + enrichment + AI tags V2
          </span>
        </div>
        <p className="text-text-secondary">Upload a spreadsheet, preview duplicates, then review what the standard import flow will do.</p>
      </div>

      <BulkImportHelp />

      <div className="flex flex-wrap gap-2 text-sm">
        {STEP_ITEMS.map((item) => (
          <div
            key={item.key}
            className={`rounded-full px-3 py-1 ${
              step === item.key
                ? 'bg-primary-50 text-primary-700 border border-primary-200'
                : 'bg-gray-100 text-text-secondary'
            }`}
          >
            {item.label}
          </div>
        ))}
      </div>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-red-700">
          {error}
        </div>
      )}

      {pollingError && step === 'running' && (
        <div className="rounded-lg border border-yellow-200 bg-yellow-50 px-4 py-3 text-yellow-700">
          {pollingError}
        </div>
      )}

      {step === 'upload' && (
        <UploadStep
          selectedFile={selectedFile}
          loading={loading}
          onFileSelect={setSelectedFile}
          onRunDryRun={handleRunDryRun}
        />
      )}

      {step === 'dryRun' && dryRunData && (
        <DryRunStep
          dryRunData={dryRunData}
          summaryCards={summaryCards}
          onReset={resetFlow}
          onContinue={() => setStep('confirm')}
        />
      )}

      {step === 'confirm' && dryRunData && (
        <ConfirmStep
          summary={dryRunData.summary}
          summaryCards={summaryCards}
          dryRunData={dryRunData}
          loading={loading}
          onBack={() => setStep('dryRun')}
          onStartImport={handleStartImport}
        />
      )}

      {step === 'running' && (
        <ProgressStep
          activeJobStatus={activeJobStatus}
          selectedFile={selectedFile}
        />
      )}

      {step === 'results' && (
        <ResultsStep
          activeJobStatus={activeJobStatus}
          canRetry={canRetry}
          resetFlow={resetFlow}
          handleRetryFailed={handleRetryFailed}
          retryLoading={retryLoading}
          resultFilter={resultFilter}
          setResultFilter={setResultFilter}
          resultsLoading={resultsLoading}
          resultsData={resultsData}
        />
      )}
    </div>
  );
};

export default BulkImport;
